import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Check, Loader2, Gift } from "lucide-react";
import http from "@/api";
import { useApp } from "@/context";

/**
 * Upsell strip shown on the product detail page.
 * Suggests add-ons (balloons, cake, chocolate, money-with-flowers) the
 * customer can attach to the current product with one tap.
 *
 * Marketing tagline above each group encourages the addition.
 */
export default function AddOnSuggestions({ productId }) {
  const { lang, formatPrice, addToCart } = useApp();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [added, setAdded] = useState({}); // {productId: true}

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await http.get(`/products/${productId}/addons`);
        if (!cancelled) setGroups(data?.groups || []);
      } catch {
        if (!cancelled) setGroups([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [productId]);

  const onAdd = (p) => {
    addToCart(p, 1, null, []);
    setAdded((prev) => ({ ...prev, [p.id]: true }));
    setTimeout(() => {
      setAdded((prev) => {
        const n = { ...prev };
        delete n[p.id];
        return n;
      });
    }, 2500);
  };

  if (loading) {
    return (
      <div className="mt-5 flex items-center justify-center py-6 opacity-60 text-sm">
        <Loader2 size={16} className="animate-spin mr-2" />
        {lang === "ar" ? "نحضّر اقتراحات مميزة..." : "Preparing suggestions..."}
      </div>
    );
  }

  if (!groups.length) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mt-6 space-y-5"
      data-testid="product-addons"
    >
      {/* Section header with marketing message */}
      <div className="text-center px-4">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider opacity-60 mb-2">
          <Gift size={14} />
          {lang === "ar" ? "اقتراحات لإكمال هديتك" : "Complete your gift"}
        </div>
        <h3 className="text-lg sm:text-xl font-bold">
          {lang === "ar"
            ? "✨ اجعل هديتك مميزة!"
            : "✨ Make your gift extra special!"}
        </h3>
        <p className="text-sm opacity-70 mt-1">
          {lang === "ar"
            ? "أضف لمسات تجعل المتلقي يتذكر هديتك للأبد"
            : "Add touches that make your gift unforgettable"}
        </p>
      </div>

      {/* Each add-on category */}
      {groups.map((g) => (
        <div key={g.key} className="space-y-2" data-testid={`addon-group-${g.key}`}>
          <div className="px-4">
            <div className="flex items-baseline justify-between gap-3">
              <h4 className="font-bold text-base">
                {lang === "ar" ? g.title_ar : g.title_en}
              </h4>
              <span className="text-xs opacity-50">
                {g.items.length} {lang === "ar" ? "خيار" : "options"}
              </span>
            </div>
            <p className="text-xs text-emerald-700 dark:text-emerald-300 italic mt-0.5">
              {lang === "ar" ? g.tagline_ar : g.tagline_en}
            </p>
          </div>

          {/* Horizontal scroll of add-on cards */}
          <div className="flex gap-3 overflow-x-auto px-4 pb-2 snap-x snap-mandatory scrollbar-hide">
            {g.items.map((p) => {
              const name = lang === "ar" ? p.name_ar : p.name_en;
              const isAdded = !!added[p.id];
              return (
                <div
                  key={p.id}
                  className="snap-start flex-shrink-0 w-32 sm:w-36 rounded-xl overflow-hidden border bg-white shadow-sm hover:shadow-md transition-shadow"
                  style={{ borderColor: "var(--border)" }}
                  data-testid={`addon-item-${p.id}`}
                >
                  <div className="relative aspect-square bg-gray-50">
                    {p.image && (
                      <img
                        src={p.image}
                        alt={name}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <div className="p-2 space-y-1.5">
                    <div className="text-xs line-clamp-2 leading-tight min-h-[2rem]">
                      {name}
                    </div>
                    <div className="text-sm font-bold" style={{ color: "var(--accent)" }}>
                      {formatPrice(p.price_sar)}
                    </div>
                    <button
                      onClick={() => onAdd(p)}
                      disabled={isAdded}
                      className={`w-full text-xs py-1.5 rounded-lg font-semibold flex items-center justify-center gap-1 transition-all ${
                        isAdded
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-emerald-900 text-white hover:bg-emerald-800 active:scale-95"
                      }`}
                      data-testid={`addon-add-btn-${p.id}`}
                    >
                      {isAdded ? (
                        <>
                          <Check size={12} />
                          {lang === "ar" ? "أُضيف" : "Added"}
                        </>
                      ) : (
                        <>
                          <Plus size={12} />
                          {lang === "ar" ? "أضف" : "Add"}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Encouraging footer */}
      <div className="mx-4 p-4 rounded-xl bg-gradient-to-r from-emerald-50 to-amber-50 dark:from-emerald-950/30 dark:to-amber-950/30 border border-emerald-200/50 text-center">
        <p className="text-sm font-semibold">
          {lang === "ar"
            ? "🎁 الهدايا الكاملة تترك أثراً يدوم"
            : "🎁 Complete gifts leave a lasting impression"}
        </p>
        <p className="text-xs opacity-70 mt-1">
          {lang === "ar"
            ? "العملاء الذين يضيفون 2+ من اللمسات يحصلون على تقييمات أعلى من المتلقّين"
            : "Customers who add 2+ extras get higher reviews from recipients"}
        </p>
      </div>
    </motion.div>
  );
}
